/**
 * Clipboard Store
 * Manages copy-paste functionality for tracks between History and Workbench views
 * Persists clipboard to localStorage with expiration (1 hour)
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Track } from '../api/types';

/**
 * Clipboard state including metadata for persistence
 */
interface ClipboardState {
  tracks: Track[];
  timestamp: number; // When tracks were copied (for expiration)
}

/**
 * Clipboard store interface
 */
export interface ClipboardStore {
  // State
  tracks: Track[];

  // Actions
  setTracks: (tracks: Track[]) => void;
  clearTracks: () => void;
  hasTracks: () => boolean;
  getTrackCount: () => number;
}

// Expiration time in milliseconds (1 hour)
const CLIPBOARD_EXPIRATION_MS = 60 * 60 * 1000;

/**
 * Check if clipboard is expired based on timestamp
 */
function isClipboardExpired(timestamp: number): boolean {
  return Date.now() - timestamp > CLIPBOARD_EXPIRATION_MS;
}

/**
 * Clipboard store with localStorage persistence
 */
export const useClipboardStore = create<ClipboardStore>()(
  persist(
    (set, get) => ({
      // Initial state
      tracks: [],

      // Set tracks from history selection
      setTracks: (newTracks) => {
        set({
          tracks: newTracks,
        });
      },

      // Clear clipboard
      clearTracks: () => {
        set({
          tracks: [],
        });
      },

      // Check if clipboard has tracks (and not expired)
      hasTracks: () => {
        const { tracks } = get();
        return tracks.length > 0;
      },

      // Get count of tracks in clipboard
      getTrackCount: () => {
        return get().tracks.length;
      },
    }),
    {
      name: 'splister-clipboard', // localStorage key
      storage: createJSONStorage(() => localStorage),
      // Custom serialize/deserialize to handle expiration
      migrate: (persistedState: unknown, _version: number) => {
        // Type guard for persisted state
        if (
          typeof persistedState === 'object' &&
          persistedState !== null &&
          'state' in persistedState
        ) {
          const state = persistedState as { state: ClipboardState };
          // Check if clipboard is expired
          if (isClipboardExpired(state.state.timestamp)) {
            // Return empty clipboard if expired
            return {
              tracks: [],
            } as unknown as ClipboardStore;
          }
        }
        return persistedState as ClipboardStore;
      },
    }
  )
);
